import { type TypedDocumentNode } from '@graphql-typed-document-node/core'
import { useAsyncFn, useMountedState } from 'react-use'

import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { exists } from '~/utils'

const refetchIntervalMilliseconds = 1000
const maxConsecutiveErrorCount = 3

export const useTabulate = <
  QueryData,
  Mutation extends {
    count?: { awaitId?: string | null | undefined } | null | undefined
  },
  MutationVariables,
>(
  mutationDocument: TypedDocumentNode<Mutation, MutationVariables>,
  queryDocument: TypedDocumentNode<
    {
      count?:
        | { arrived: boolean; result?: QueryData | null | undefined }
        | null
        | undefined
    },
    {
      awaitId: string
    }
  >,
) => {
  const isMounted = useMountedState()
  const graphQLRequest = useGraphQLRequest()

  return useAsyncFn(
    async (variables: MutationVariables) => {
      const mutationResult = await graphQLRequest({
        document: mutationDocument,
        variables: variables as Record<string, unknown>,
      })

      const awaitId = mutationResult.count?.awaitId
      if (!exists(awaitId)) {
        return null
      }

      return await new Promise<QueryData | null>((resolve) => {
        // ポーリング中の一時的な通信エラーは一定回数までリトライで吸収し、
        // 連続して失敗し続けた場合はresolve(null)でローディングを終了させる。
        // (rejectするとreact-useのuseAsyncFnがそのErrorを解決値として返し、呼び出し側が
        //  集計データとして書き込んでしまうため、unmount時と揃えてnullで畳む。
        //  Promiseをsettleしないと永遠にpendingのままローディングが消えなくなる)
        let consecutiveErrorCount = 0

        const refetchUntilArrived = async () => {
          if (!isMounted()) {
            resolve(null)

            return
          }

          let queryResult
          try {
            queryResult = await graphQLRequest({
              document: queryDocument,
              variables: { awaitId },
            })
          } catch {
            consecutiveErrorCount += 1
            if (consecutiveErrorCount >= maxConsecutiveErrorCount) {
              resolve(null)

              return
            }

            setTimeout(refetchUntilArrived, refetchIntervalMilliseconds)

            return
          }

          consecutiveErrorCount = 0

          const arrived = queryResult.count?.arrived

          if (!arrived) {
            setTimeout(refetchUntilArrived, refetchIntervalMilliseconds)

            return
          }

          const result = queryResult.count?.result ?? null

          resolve(result)
        }

        refetchUntilArrived()
      })
    },
    [graphQLRequest, isMounted, mutationDocument, queryDocument],
  )
}
